import React, { useEffect, useRef, useState } from "react";
import { CalendarDays, Clock4, MapPin, Users2, Loader2, Check } from "lucide-react";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "./ui/carousel";
import {
  FORMAT_LABEL, formatStartDate, isFull, monthFilters, periodLabel,
  scheduleLabel, selectedIndex, startsInMonth, statusBadge,
} from "../lib/groupFormat";
import TrainerLine from "./TrainerLine";

/**
 * The list of cohorts, and nothing else.
 *
 * The cohort list used to be fetched here, which also made this the only place
 * that knew whether a `?grupa=<id>` still named an open cohort. Checkout needs
 * that answer before it decides whether to show this list at all, so the fetch
 * moved up to the page and arrives as `groups` (null while it loads).
 */
export default function GroupSelector({ groups, error = null, value, onChange, allowSkip = true }) {
  // Same filter as the homepage schedule, same rules: nothing chosen shows
  // everything, and one month is a label rather than a filter (23.09.2026).
  const [month, setMonth] = useState("");
  // The carousel's own handle, so the cohort chosen on the homepage can be
  // scrolled into view instead of waiting behind two arrow presses.
  const [carouselApi, setCarouselApi] = useState(null);
  const cardRefs = useRef({});

  const months = monthFilters(groups || []);
  const showFilters = months.length > 1;
  const activeMonth = showFilters && months.some((m) => m.value === month) ? month : "";
  const visible = (groups || []).filter((g) => startsInMonth(g, activeMonth));
  // Three or more cohorts ride in the carousel; fewer sit in the grid, which
  // scrolls by itself on a phone. Which one is on screen decides how the
  // selected cohort is brought into view.
  const carousel = visible.length >= 3;

  // Bring the selected cohort into view: in the carousel by index, in the
  // scrolling row by the card itself. Runs when the list, the filter or the
  // selection changes — a cohort chosen on the homepage is selected before
  // this component has even loaded its own list.
  useEffect(() => {
    if (!value) return;
    const index = selectedIndex(visible, value);
    if (index < 0) return;
    if (carousel && carouselApi) {
      carouselApi.scrollTo(index, true);
      return;
    }
    const card = cardRefs.current[value];
    if (card?.scrollIntoView) card.scrollIntoView({ block: "nearest", inline: "center" });
    // `visible` is rebuilt on every render; its identity is not the signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, carousel, carouselApi, groups, activeMonth]);

  // The error comes before the loading state on purpose: a failed request
  // leaves no list, so the two conditions are true together and whichever is
  // read first wins. Loading won, and the spinner span forever with the
  // reason on the floor.
  if (error) {
    return (
      <div className="rounded-2xl border border-[#E63946]/30 bg-[#E63946]/5 p-5 text-sm text-[#9b1f2c]" data-testid="groups-error">
        {error}
      </div>
    );
  }

  if (!Array.isArray(groups)) {
    return (
      <div className="flex items-center gap-2 text-[#6B7280] text-sm py-6" data-testid="groups-loading">
        <Loader2 size={16} className="animate-spin" /> Se încarcă grupele disponibile…
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 text-sm text-[#373A45]" data-testid="groups-empty">
        <div className="font-display text-base font-semibold text-[#373A45]">
          Grupele vor fi anunțate după centralizarea înscrierilor.
        </div>
        <p className="mt-3 text-[#6B7280]">
          Poți trimite înscrierea chiar dacă momentan nu există grupe active.
          Preferințele tale ne vor ajuta să organizăm viitoarele serii.
        </p>
      </div>
    );
  }

  // Use a carousel when there are 3+ groups so the grid doesn't stretch
  // vertically. Two-or-fewer groups fall back to the original 2-column grid
  // (cleaner, no carousel chrome when not needed).
  const filterBar = showFilters && (
    <div
      className="mb-3 -mx-1 px-1 flex gap-2 overflow-x-auto sm:flex-wrap"
      role="group"
      aria-label="Filtrează grupele după luna de start"
      data-testid="group-month-filters"
    >
      {[{ value: "", label: "Toate", count: groups.length }, ...months].map((m) => {
        const on = m.value === activeMonth;
        return (
          <button
            key={m.value || "all"}
            type="button"
            onClick={() => setMonth(m.value === activeMonth ? "" : m.value)}
            aria-pressed={on}
            className={`shrink-0 rounded-full border px-3.5 py-1 text-xs font-semibold transition-colors ${
              on
                ? "border-[#965EA7] bg-[#965EA7] text-white"
                : "border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]/50 hover:bg-[#F0E7F4]/40"
            }`}
            data-testid={`group-month-${m.value || "all"}`}
          >
            {m.label} <span className="tabular-nums opacity-70">({m.count})</span>
          </button>
        );
      })}
    </div>
  );

  const cardClass = (g, selected, disabled) => [
    "group relative text-left rounded-2xl border p-5 transition-all w-full h-full flex flex-col",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#965EA7]/60",
    disabled
      ? "opacity-60 cursor-not-allowed border-[#E5E7EB] bg-white"
      : selected
      ? "border-[#965EA7] bg-[#965EA7]/[0.04] ring-2 ring-[#965EA7]/25 shadow-[0_4px_18px_rgba(150,94,167,0.15)]"
      : "border-[#E5E7EB] bg-white hover:border-[#965EA7]/60 hover:-translate-y-0.5 hover:shadow-md",
  ].join(" ");

  const renderCard = (g) => {
    // May be null — see `statusBadge`. Fullness is read on its own so a
    // cohort that publishes no counts is still refused when it is full.
    const badge = statusBadge(g);
    const selected = value === g.id;
    const disabled = isFull(g);
    return (
      <button
        key={g.id}
        type="button"
        role="radio"
        aria-checked={selected}
        aria-disabled={disabled}
        disabled={disabled}
        // The full group goes out alongside the id so the page can show what
        // was chosen without fetching the list a second time. Callers that only
        // need the id keep working — a React setter ignores the extra argument.
        onClick={() => !disabled && onChange(g.id, g)}
        data-testid={`group-card-${g.id}`}
        className={cardClass(g, selected, disabled)}
      >
        <div className="flex flex-col gap-2">
          <div className="font-display text-base font-bold text-[#373A45] leading-snug" style={{ wordBreak: "normal", overflowWrap: "normal" }}>
            {g.name}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-[#6B7280]">
              {periodLabel(g.start_date, g.end_date)}
              {/* A cohort with its own price says so where it is chosen (09.10.2026). */}
              {Number(g.price) > 0 && (
                <span className="ml-2 font-semibold text-[#373A45]" data-testid={`group-price-${g.id}`}>
                  {Number(g.price).toLocaleString("ro-RO")} lei
                </span>
              )}
            </div>
            {badge ? (
              <span
                className={`inline-flex items-center gap-1 shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none whitespace-nowrap ${badge.cls}`}
                data-testid={`group-status-${g.id}`}
              >
                {selected && !disabled && (
                  <Check size={12} strokeWidth={3} aria-hidden="true" className="-ml-0.5" />
                )}
                <span>{badge.label}</span>
              </span>
            ) : selected && (
              /* With no badge to carry it, the tick still has to appear
                 somewhere — it is the only sign the card is the chosen one
                 besides the border. */
              <span
                className="inline-flex items-center gap-1 shrink-0 rounded-full bg-[#965EA7]/12 text-[#7A4986] px-2.5 py-1 text-[11px] font-semibold leading-none"
                data-testid={`group-selected-${g.id}`}
              >
                <Check size={12} strokeWidth={3} aria-hidden="true" /> Selectată
              </span>
            )}
          </div>
        </div>

        <TrainerLine trainer={g.trainer} pending={g.trainer_pending} compact className="mt-3" testid={`group-trainer-${g.id}`} />

        <dl className="mt-4 space-y-2 text-sm text-[#373A45] flex-1">
          <div className="flex items-center gap-2">
            <CalendarDays size={15} className="text-[#965EA7] shrink-0" />
            <span><strong>Start:</strong> {formatStartDate(g.start_date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock4 size={15} className="text-[#3F8EBA] shrink-0" />
            <span>{scheduleLabel(g)}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={15} className="text-[#519872] shrink-0" />
            <span>{FORMAT_LABEL[g.format] || g.format}</span>
          </div>
          {/* Only for a cohort that publishes its counts — the fields are
              absent from the response otherwise, not merely unpainted. */}
          {typeof g.enrolled_count === "number" && (
            <div className="flex items-center gap-2">
              <Users2 size={15} className="text-[#6B7280] shrink-0" />
              <span>
                {g.enrolled_count}/{g.max_seats} înscriși
              </span>
            </div>
          )}
        </dl>
      </button>
    );
  };

  return (
    <div data-testid="group-selector">
      {filterBar}
      {carousel ? (
        <Carousel
          key={activeMonth || "all"}
          setApi={setCarouselApi}
          opts={{ align: "start", loop: false, slidesToScroll: 1 }}
          className="w-full"
        >
          {/* Negative-margin trick to align inner slides with the outer container. */}
          <CarouselContent className="-ml-3" viewportClassName="py-2 -my-2">
            {visible.map((g) => (
              <CarouselItem
                key={g.id}
                /* Every breakpoint keeps part of the next card in view. It was
                   `basis-full` on mobile, where the arrows are also hidden — so
                   a phone showed one cohort with nothing suggesting others. */
                className="pl-3 basis-[88%] md:basis-[70%] lg:basis-1/2"
              >
                <div className="h-full">{renderCard(g)}</div>
              </CarouselItem>
            ))}
          </CarouselContent>
          {/* Arrows sit below-right rather than overlapping the cards, so they
              never clip on narrow viewports.

              They carry the accent by default, not only on hover: with more
              cohorts than fit on screen, these are the sole indication the
              others exist. A grey outline reads as decoration and gets
              skipped — someone then picks from the two they can see, unaware
              of a third. The count beside them says how many there are without
              making anyone count cards. */}
          <div className="hidden md:flex items-center justify-end gap-3 mt-3">
            <span className="text-xs text-[#6B7280]" data-testid="group-carousel-count">
              {visible.length} {visible.length === 1 ? "grupă disponibilă" : "grupe disponibile"}
            </span>
            <div className="flex gap-2">
              <CarouselPrevious
                className="static translate-y-0 h-10 w-10 border-2 border-[#965EA7]/45 text-[#965EA7] bg-white
                           hover:bg-[#965EA7] hover:text-white hover:border-[#965EA7]
                           focus-visible:ring-2 focus-visible:ring-[#965EA7]/50
                           disabled:opacity-30 disabled:border-[#E5E7EB] disabled:text-[#9CA3AF]
                           transition-colors"
                data-testid="group-carousel-prev"
              />
              <CarouselNext
                className="static translate-y-0 h-10 w-10 border-2 border-[#965EA7]/45 text-[#965EA7] bg-white
                           hover:bg-[#965EA7] hover:text-white hover:border-[#965EA7]
                           focus-visible:ring-2 focus-visible:ring-[#965EA7]/50
                           disabled:opacity-30 disabled:border-[#E5E7EB] disabled:text-[#9CA3AF]
                           transition-colors"
                data-testid="group-carousel-next"
              />
            </div>
          </div>
        </Carousel>
      ) : (
        /* Below `md` this scrolls horizontally with the next card peeking in;
           from `md` up it is the plain two-column grid it always was.
           A vertical stack gave no hint a second cohort existed — on a phone
           the first card fills the viewport and the one below it is invisible,
           so people chose from an option set of one without knowing. */
        <div
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory -mx-1 px-1 -my-2 py-2
                     md:grid md:grid-cols-2 md:overflow-visible md:mx-0 md:px-0
                     items-stretch"
          data-testid="groups-grid"
        >
          {visible.map((g) => (
            <div
              key={g.id}
              ref={(el) => { cardRefs.current[g.id] = el; }}
              className="snap-start shrink-0 basis-[88%] md:basis-auto md:shrink"
            >
              {renderCard(g)}
            </div>
          ))}
        </div>
      )}

      {allowSkip && (
        <button
          type="button"
          onClick={() => onChange(null, null)}
          className={[
            "mt-3 w-full text-left rounded-xl border p-4 text-sm transition-all",
            value === null
              ? "border-[#373A45] bg-[#373A45]/5 text-[#373A45]"
              : "border-dashed border-[#E5E7EB] text-[#6B7280] hover:border-[#373A45]/40 hover:text-[#373A45]",
          ].join(" ")}
          data-testid="group-skip"
        >
          Nu sunt sigur(ă) încă — vreau doar să primesc broșura și să fiu anunțat(ă) la deschiderea următoarei cohorte.
        </button>
      )}
    </div>
  );
}
